import { getZonedParts } from './format';
import { normalizeSearchText } from './richtext/plain-text';

/**
 * The search query syntax (PLAN §3.1 "Query parsing and filter mapping", §4 "Search query
 * syntax"; SPEC §4.4). `parseSearchQuery` splits what the person typed into free-text terms and
 * modifiers, so the API and the web UI (filter chips, underlined unknown names) read the query
 * the same way. Pure: no I/O, no clock (relative periods are resolved by `searchTimeBounds`, which
 * takes `now`).
 *
 * Free text: `word`, `"exact phrase"`, `-word` / `-"phrase"` (exclusion), `a or b`, `word*`
 * (prefix). It becomes `text` for `websearch_to_tsquery('simple', hub_unaccent(text))` plus
 * `prefixTerms` for `to_tsquery('simple', hub_unaccent(term) || ':*')`. Search-as-you-type: when
 * the input does not end with whitespace, its last bare word is matched as a prefix instead of a
 * whole word (it moves from `text` to `prefixTerms`). Prefix terms keep only letters, combining
 * marks and digits (`\p{L}\p{M}\p{N}`) and need at least 2 characters, so Devanagari vowel signs
 * and viramas stay part of the word.
 *
 * Modifiers (the key is case-insensitive; a value may be quoted, `from:"Mohit Sharma"`):
 *
 * | Modifier | Values |
 * |---|---|
 * | `from:` / `mentions:` | `me`, `@name`, `name`, `<@u:ID>` |
 * | `in:` | `#channel`, `channel`, `@person` (the DM with them), `<#c:ID>`, `<@u:ID>` |
 * | `before:` / `after:` / `on:` | `YYYY-MM-DD` (workspace timezone; `before` and `after` exclusive) |
 * | `during:` | `YYYY-MM-DD`, `YYYY-MM`, `YYYY`, `today`, `yesterday`, `week`, `month` |
 * | `has:` | `file`, `link`, `task` (Phase 4), `pin`, `reaction` |
 * | `is:` | `thread`, `saved` |
 *
 * Combination rules: repeated `from:`, `in:` and `mentions:` are ORed within their kind; `has:` and
 * `is:` values are each their own predicate (ANDed); date modifiers intersect. Different kinds
 * are ANDed. An unknown key (`foo:bar`, `https://…`) is free text. A known key with an invalid
 * value is reported in `errors` (the API answers `400 search_query_invalid` with
 * `details.position`). Everything is NFC-normalised with zero-width characters removed first
 * (D-050), per token, so offsets stay offsets into the raw input.
 */

// ---------------------------------------------------------------- types

/** A UTF-16 range `[start, end)` into the raw query. */
export interface SearchSpan {
  start: number;
  end: number;
}

export type SearchPersonRef =
  { kind: 'me' } | { kind: 'id'; id: string } | { kind: 'name'; name: string };

export type SearchConversationRef =
  | { kind: 'channel'; name: string }
  | { kind: 'id'; id: string }
  | { kind: 'dm'; person: Exclude<SearchPersonRef, { kind: 'me' }> };

export type SearchPeriod =
  | { kind: 'day'; date: string }
  | { kind: 'month'; year: number; month: number }
  | { kind: 'year'; year: number }
  | { kind: 'relative'; value: SearchRelativePeriod };

export const SEARCH_RELATIVE_PERIODS = ['today', 'yesterday', 'week', 'month'] as const;
export type SearchRelativePeriod = (typeof SEARCH_RELATIVE_PERIODS)[number];

export const SEARCH_HAS_VALUES = ['file', 'link', 'task', 'pin', 'reaction'] as const;
export type SearchHasValue = (typeof SEARCH_HAS_VALUES)[number];

export const SEARCH_IS_VALUES = ['thread', 'saved'] as const;
export type SearchIsValue = (typeof SEARCH_IS_VALUES)[number];

export const SEARCH_MODIFIER_KEYS = [
  'from',
  'in',
  'mentions',
  'before',
  'after',
  'on',
  'during',
  'has',
  'is',
] as const;
export type SearchModifierKey = (typeof SEARCH_MODIFIER_KEYS)[number];

interface FilterBase extends SearchSpan {
  /** The token as typed (for chips). */
  raw: string;
}

export type SearchFilter =
  | (FilterBase & { type: 'from'; person: SearchPersonRef })
  | (FilterBase & { type: 'mentions'; person: SearchPersonRef })
  | (FilterBase & { type: 'in'; target: SearchConversationRef })
  | (FilterBase & { type: 'before'; date: string })
  | (FilterBase & { type: 'after'; date: string })
  | (FilterBase & { type: 'on'; date: string })
  | (FilterBase & { type: 'during'; period: SearchPeriod })
  | (FilterBase & { type: 'has'; value: SearchHasValue })
  | (FilterBase & { type: 'is'; value: SearchIsValue });

export type SearchFilterType = SearchFilter['type'];

export interface SearchTerm extends SearchSpan {
  kind: 'word' | 'phrase' | 'or';
  /** Normalised (NFC, no zero-width characters); phrases without their quotes. */
  value: string;
  negated: boolean;
  /** Written `word*`, or the unfinished last word (search-as-you-type). */
  prefix: boolean;
}

export type SearchQueryErrorCode = 'invalid_date' | 'invalid_value' | 'empty_value';

export interface SearchQueryError extends SearchSpan {
  code: SearchQueryErrorCode;
  /** The key, e.g. `before`. */
  modifier: SearchModifierKey;
  token: string;
  message: string;
}

export interface ParsedSearchQuery {
  raw: string;
  terms: SearchTerm[];
  /**
   * Input for `websearch_to_tsquery('simple', hub_unaccent(text))`: words, quoted phrases,
   * `-exclusions` and `or`, without the prefix terms. `''` when there is none.
   */
  text: string;
  /** Terms matched as prefixes (`to_tsquery(term || ':*')`, ANDed), in order. */
  prefixTerms: string[];
  filters: SearchFilter[];
  errors: SearchQueryError[];
  /** No free text and no filters: nothing to search for. */
  isEmpty: boolean;
}

export interface ParseSearchQueryOptions {
  /**
   * Treat the last bare word as unfinished when the input does not end with whitespace (default
   * true: the search box searches as you type). Submitted queries may pass false.
   */
  searchAsYouType?: boolean;
}

// ---------------------------------------------------------------- tokenizer

interface RawToken extends SearchSpan {
  text: string;
}

const WHITESPACE = /\s/u;

/** Splits on whitespace; `"…"` groups spaces (an unclosed quote runs to the end). */
function tokenize(input: string): RawToken[] {
  const tokens: RawToken[] = [];
  let i = 0;
  while (i < input.length) {
    while (i < input.length && WHITESPACE.test(input[i]!)) i++;
    if (i >= input.length) break;
    const start = i;
    let inQuote = false;
    while (i < input.length) {
      const char = input[i]!;
      if (char === '"') inQuote = !inQuote;
      else if (!inQuote && WHITESPACE.test(char)) break;
      i++;
    }
    tokens.push({ text: input.slice(start, i), start, end: i });
  }
  return tokens;
}

/** Removes one pair of surrounding quotes (or a lone opening quote). */
function unquote(value: string): string {
  let v = value;
  if (v.startsWith('"')) v = v.slice(1);
  if (v.endsWith('"')) v = v.slice(0, -1);
  return v;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const USER_TOKEN = /^<@u:([0-9a-f-]{36})>$/i;
const CONVERSATION_TOKEN = /^<#c:([0-9a-f-]{36})>$/i;
const MODIFIER = /^([A-Za-z]+):(.*)$/su;
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH = /^(\d{4})-(\d{2})$/;
const YEAR = /^(\d{4})$/;
/** What a prefix term keeps (letters in any script, combining marks, digits). */
const NON_LEXEME = /[^\p{L}\p{M}\p{N}]+/gu;

/** A real calendar date `YYYY-MM-DD` (2000-02-30 is refused). */
export function isCalendarDate(value: string): boolean {
  const match = DAY.exec(value);
  if (!match) return false;
  const [, y, m, d] = match.map(Number) as [number, number, number, number];
  if (m < 1 || m > 12 || d < 1) return false;
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return d <= days;
}

function isModifierKey(key: string): key is SearchModifierKey {
  return (SEARCH_MODIFIER_KEYS as readonly string[]).includes(key);
}

function personRef(value: string): SearchPersonRef | null {
  const user = USER_TOKEN.exec(value);
  if (user && UUID.test(user[1]!)) return { kind: 'id', id: user[1]!.toLowerCase() };
  const name = unquote(value.startsWith('@') ? value.slice(1) : value).trim();
  if (!name) return null;
  if (!value.startsWith('@') && name.toLowerCase() === 'me') return { kind: 'me' };
  return { kind: 'name', name };
}

function conversationRef(value: string): SearchConversationRef | null {
  const conv = CONVERSATION_TOKEN.exec(value);
  if (conv && UUID.test(conv[1]!)) return { kind: 'id', id: conv[1]!.toLowerCase() };
  const user = USER_TOKEN.exec(value);
  if (user && UUID.test(user[1]!))
    return { kind: 'dm', person: { kind: 'id', id: user[1]!.toLowerCase() } };
  if (value.startsWith('@')) {
    const name = unquote(value.slice(1)).trim();
    return name ? { kind: 'dm', person: { kind: 'name', name } } : null;
  }
  const name = unquote(value.startsWith('#') ? value.slice(1) : value)
    .trim()
    .toLowerCase();
  return name ? { kind: 'channel', name } : null;
}

function period(value: string): SearchPeriod | null {
  const lower = value.toLowerCase();
  if ((SEARCH_RELATIVE_PERIODS as readonly string[]).includes(lower)) {
    return { kind: 'relative', value: lower as SearchRelativePeriod };
  }
  if (isCalendarDate(value)) return { kind: 'day', date: value };
  const month = MONTH.exec(value);
  if (month) {
    const m = Number(month[2]);
    return m >= 1 && m <= 12 ? { kind: 'month', year: Number(month[1]), month: m } : null;
  }
  const year = YEAR.exec(value);
  if (year) return { kind: 'year', year: Number(year[1]) };
  return null;
}

// ---------------------------------------------------------------- parser

export function parseSearchQuery(
  raw: string,
  options: ParseSearchQueryOptions = {},
): ParsedSearchQuery {
  const searchAsYouType = options.searchAsYouType ?? true;
  const terms: SearchTerm[] = [];
  const filters: SearchFilter[] = [];
  const errors: SearchQueryError[] = [];

  for (const token of tokenize(raw)) {
    const text = normalizeSearchText(token.text);
    if (!text) continue;
    const span = { start: token.start, end: token.end };
    const modifier = MODIFIER.exec(text);
    const key = modifier?.[1]?.toLowerCase();
    if (modifier && key && isModifierKey(key)) {
      const value = modifier[2]!;
      const fail = (code: SearchQueryErrorCode, message: string) =>
        errors.push({ code, modifier: key, token: token.text, message, ...span });
      if (value === '' || value === '"' || value === '""') {
        fail('empty_value', `Add a value after ${key}:`);
        continue;
      }
      const base = { raw: token.text, ...span };
      switch (key) {
        case 'from':
        case 'mentions': {
          const person = personRef(value);
          if (person) filters.push({ ...base, type: key, person });
          else fail('empty_value', `Add a name after ${key}:`);
          break;
        }
        case 'in': {
          const target = conversationRef(value);
          if (target) filters.push({ ...base, type: 'in', target });
          else fail('empty_value', 'Add a channel or person after in:');
          break;
        }
        case 'before':
        case 'after':
        case 'on': {
          const date = unquote(value);
          if (isCalendarDate(date)) filters.push({ ...base, type: key, date });
          else fail('invalid_date', `Use a date like 2026-09-15 after ${key}:`);
          break;
        }
        case 'during': {
          const p = period(unquote(value));
          if (p) filters.push({ ...base, type: 'during', period: p });
          else
            fail(
              'invalid_date',
              'Use during:today, yesterday, week, month, or a date like 2026-09 or 2026-09-15',
            );
          break;
        }
        case 'has': {
          const v = unquote(value).toLowerCase();
          if ((SEARCH_HAS_VALUES as readonly string[]).includes(v)) {
            filters.push({ ...base, type: 'has', value: v as SearchHasValue });
          } else fail('invalid_value', `has: takes ${SEARCH_HAS_VALUES.join(', ')}`);
          break;
        }
        case 'is': {
          const v = unquote(value).toLowerCase();
          if ((SEARCH_IS_VALUES as readonly string[]).includes(v)) {
            filters.push({ ...base, type: 'is', value: v as SearchIsValue });
          } else fail('invalid_value', `is: takes ${SEARCH_IS_VALUES.join(', ')}`);
          break;
        }
      }
      continue;
    }

    // Free text.
    const negated = text.startsWith('-') && text.length > 1;
    const body = negated ? text.slice(1) : text;
    if (body.startsWith('"')) {
      const phrase = unquote(body).trim();
      if (phrase) terms.push({ kind: 'phrase', value: phrase, negated, prefix: false, ...span });
      continue;
    }
    if (!negated && body.toLowerCase() === 'or') {
      terms.push({ kind: 'or', value: 'or', negated: false, prefix: false, ...span });
      continue;
    }
    const prefix = !negated && body.length > 1 && body.endsWith('*');
    const value = prefix ? body.replace(/\*+$/, '') : body;
    if (value) terms.push({ kind: 'word', value, negated, prefix, ...span });
  }

  // Search-as-you-type: the unfinished last bare word is a prefix.
  const last = terms[terms.length - 1];
  if (
    searchAsYouType &&
    last &&
    last.kind === 'word' &&
    !last.negated &&
    last.end === raw.length &&
    !filters.some((f) => f.start > last.start)
  ) {
    last.prefix = true;
  }

  const prefixTerms: string[] = [];
  const textParts: string[] = [];
  for (const term of terms) {
    if (term.kind === 'word' && term.prefix) {
      const lexeme = term.value.replace(NON_LEXEME, '');
      if (lexeme.length >= 2) {
        prefixTerms.push(lexeme);
        continue;
      }
      // Too short to be a useful prefix: keep it as an ordinary word.
      term.prefix = false;
    }
    if (term.kind === 'or') textParts.push('or');
    else if (term.kind === 'phrase')
      textParts.push(`${term.negated ? '-' : ''}"${term.value.replaceAll('"', ' ')}"`);
    else textParts.push(`${term.negated ? '-' : ''}${term.value}`);
  }
  // A dangling `or` at either end means nothing to websearch_to_tsquery; drop it.
  while (textParts[0] === 'or') textParts.shift();
  while (textParts[textParts.length - 1] === 'or') textParts.pop();

  const text = textParts.join(' ');
  return {
    raw,
    terms,
    text,
    prefixTerms,
    filters,
    errors,
    isEmpty: text === '' && prefixTerms.length === 0 && filters.length === 0,
  };
}

// ---------------------------------------------------------------- filter helpers

/** Filters of one type, e.g. every `from:`. */
export function filtersOfType<T extends SearchFilterType>(
  parsed: Pick<ParsedSearchQuery, 'filters'>,
  type: T,
): Extract<SearchFilter, { type: T }>[] {
  return parsed.filters.filter((f): f is Extract<SearchFilter, { type: T }> => f.type === type);
}

/** Distinct `has:` values. */
export function hasValues(parsed: Pick<ParsedSearchQuery, 'filters'>): SearchHasValue[] {
  return [...new Set(filtersOfType(parsed, 'has').map((f) => f.value))];
}

/** Distinct `is:` values. */
export function isValues(parsed: Pick<ParsedSearchQuery, 'filters'>): SearchIsValue[] {
  return [...new Set(filtersOfType(parsed, 'is').map((f) => f.value))];
}

// ---------------------------------------------------------------- time bounds (workspace timezone)

export interface SearchTimeBounds {
  /** Inclusive lower bound (`created_at >= from`). */
  from: Date | null;
  /** Exclusive upper bound (`created_at < to`). */
  to: Date | null;
  /** The ranges do not overlap: nothing can match. */
  empty: boolean;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** `YYYY-MM-DD` plus `days` (calendar arithmetic, no timezone). */
export function addCalendarDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/**
 * The UTC instant of local midnight at the start of `date` in `timeZone` (wall clock → instant,
 * iterating on the zone offset, which handles DST changes).
 */
export function zonedStartOfDay(date: string, timeZone: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const target = Date.UTC(y, m - 1, d, 0, 0, 0);
  let guess = target;
  for (let i = 0; i < 4; i++) {
    const p = getZonedParts(guess, timeZone);
    const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    const diff = wall - target;
    if (diff === 0) break;
    guess -= diff;
  }
  return new Date(guess);
}

function localToday(now: Date, timeZone: string): string {
  const p = getZonedParts(now, timeZone);
  return `${String(p.year).padStart(4, '0')}-${pad2(p.month)}-${pad2(p.day)}`;
}

/** ISO weekday 1 (Monday) … 7 (Sunday) of a calendar date. */
function isoWeekday(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return day === 0 ? 7 : day;
}

/** `[startDate, endDate)` as calendar dates for one date filter. */
function dateRange(
  filter: SearchFilter,
  now: Date,
  timeZone: string,
): [string | null, string | null] {
  switch (filter.type) {
    case 'before':
      return [null, filter.date];
    case 'after':
      return [addCalendarDays(filter.date, 1), null];
    case 'on':
      return [filter.date, addCalendarDays(filter.date, 1)];
    case 'during': {
      const p = filter.period;
      if (p.kind === 'day') return [p.date, addCalendarDays(p.date, 1)];
      if (p.kind === 'month') {
        const next = p.month === 12 ? `${p.year + 1}-01-01` : `${p.year}-${pad2(p.month + 1)}-01`;
        return [`${p.year}-${pad2(p.month)}-01`, next];
      }
      if (p.kind === 'year') return [`${p.year}-01-01`, `${p.year + 1}-01-01`];
      const today = localToday(now, timeZone);
      switch (p.value) {
        case 'today':
          return [today, addCalendarDays(today, 1)];
        case 'yesterday':
          return [addCalendarDays(today, -1), today];
        case 'week': {
          const monday = addCalendarDays(today, 1 - isoWeekday(today));
          return [monday, addCalendarDays(monday, 7)];
        }
        case 'month': {
          const [y, m] = today.split('-').map(Number) as [number, number];
          const next = m === 12 ? `${y + 1}-01-01` : `${y}-${pad2(m + 1)}-01`;
          return [`${y}-${pad2(m)}-01`, next];
        }
      }
      return [null, null];
    }
    default:
      return [null, null];
  }
}

/**
 * The `created_at` window of every date modifier, intersected, as instants in the workspace
 * timezone (`calendar.timezone`): `before:D` is before local midnight starting D, `after:D` from
 * local midnight starting D + 1 (the date itself excluded), `on:` / `during:` the whole period.
 */
export function searchTimeBounds(
  parsed: Pick<ParsedSearchQuery, 'filters'>,
  input: { now: Date; timeZone: string },
): SearchTimeBounds {
  let from: string | null = null;
  let to: string | null = null;
  for (const filter of parsed.filters) {
    const [start, end] = dateRange(filter, input.now, input.timeZone);
    if (start !== null && (from === null || start > from)) from = start;
    if (end !== null && (to === null || end < to)) to = end;
  }
  return {
    from: from === null ? null : zonedStartOfDay(from, input.timeZone),
    to: to === null ? null : zonedStartOfDay(to, input.timeZone),
    empty: from !== null && to !== null && from >= to,
  };
}

/**
 * Converts `ts_headline` output with the private-use markers U+E000 / U+E001 around matches into
 * the plain text and UTF-16 `[start, end)` highlight ranges (PLAN §3.1 "Highlighting").
 */
export const HIGHLIGHT_START = '';
export const HIGHLIGHT_END = '';

export function extractHighlights(marked: string): {
  text: string;
  highlights: [number, number][];
} {
  let text = '';
  const highlights: [number, number][] = [];
  let open: number | null = null;
  for (const char of marked) {
    if (char === HIGHLIGHT_START) {
      if (open === null) open = text.length;
    } else if (char === HIGHLIGHT_END) {
      if (open !== null && text.length > open) highlights.push([open, text.length]);
      open = null;
    } else {
      text += char;
    }
  }
  if (open !== null && text.length > open) highlights.push([open, text.length]);
  return { text, highlights };
}
